import * as SubscriptionActions from '@/store/actions/Subscription.actions';
import * as SubscriptionMutations from '@/store/mutations/Subscription.mutations';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as DatabaseService from '@/services/Database.service';
import { isObjectEmpty, dictionaryFromDocumentArray } from '@/helpers/dataHelpers';

const getInitialState = () => ({
  isSubscriptionLoading: false,
  isSubscriptionWritingData: false,
  subscriptions: {},
  activeWorkspaceSubscriptionId: null,
});

const state = getInitialState();

const getters = {
  activeWorkspaceSubscription(state) {
    return state.subscriptions[state.activeWorkspaceSubscriptionId];
  },
};

const mutations = {
  [SubscriptionMutations.SUBSCRIPTION_REQUEST_DATA](state) {
    state.isSubscriptionLoading = true;
  },
  [SubscriptionMutations.SUBSCRIPTION_BEGIN_WRITE_DATA](state) {
    state.isSubscriptionWritingData = true;
  },
  [SubscriptionMutations.SUBSCRIPTION_COMPLETE_WRITE_DATA](state) {
    state.isSubscriptionWritingData = false;
  },
  [SubscriptionMutations.RECEIVE_SUBSCRIPTIONS](state, payload) {
    if (!isObjectEmpty(payload)) {
      state.subscriptions = {
        ...state.subscriptions,
        ...payload,
      };
    }
    state.isSubscriptionLoading = false;
  },
  [SubscriptionMutations.SET_ACTIVE_WORKSPACE_SUBSCRIPTION](state, payload) {
    state.activeWorkspaceSubscriptionId = payload.subscriptionId;
  },
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  async [SubscriptionActions.FETCH_SUBSCRIPTION_BY_ID](context, payload) {
    context.commit(SubscriptionMutations.SUBSCRIPTION_REQUEST_DATA);
    try {
      const subscriptionQueryResult = await DatabaseService.getSubscriptionById(payload.subscriptionId);
      if (subscriptionQueryResult.exists) {
        const subscriptionDocument = {
          [subscriptionQueryResult.id]: { id: subscriptionQueryResult.id, ...subscriptionQueryResult.data() },
        };
        context.commit(SubscriptionMutations.RECEIVE_SUBSCRIPTIONS, subscriptionDocument);
      } else {
        context.commit(SubscriptionMutations.RECEIVE_SUBSCRIPTIONS, {});
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [SubscriptionActions.FETCH_SUBSCRIPTION_BY_WORKSPACE_ID](context, payload) {
    context.commit(SubscriptionMutations.SUBSCRIPTION_REQUEST_DATA);
    try {
      const { docs: subscriptionDocuments } = await DatabaseService.getSubscriptionByWorkspaceId(payload.workspaceId);
      const shapedSubscriptionDocuments = dictionaryFromDocumentArray(subscriptionDocuments);

      context.commit(SubscriptionMutations.RECEIVE_SUBSCRIPTIONS, shapedSubscriptionDocuments);
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [SubscriptionActions.CREATE_SUBSCRIPTION](context, payload) {
    context.commit(SubscriptionMutations.SUBSCRIPTION_BEGIN_WRITE_DATA);
    try {
      const documentCreateConfirmation = await DatabaseService.addNewSubscription(payload);
      if (documentCreateConfirmation) {
        context.commit(SubscriptionMutations.SUBSCRIPTION_COMPLETE_WRITE_DATA);
        await context.dispatch(SubscriptionActions.FETCH_SUBSCRIPTION_BY_ID, { subscriptionId: documentCreateConfirmation.id });
        context.commit(SubscriptionMutations.SET_ACTIVE_WORKSPACE_SUBSCRIPTION, { subscriptionId: documentCreateConfirmation.id });
      } else {
        throw new Error('There was an error creating subscription');
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
  async [SubscriptionActions.UPDATE_SUBSCRIPTION_BY_ID](context, payload) {
    context.commit(SubscriptionMutations.SUBSCRIPTION_BEGIN_WRITE_DATA);
    try {
      await DatabaseService.updateSubscriptionById(payload.subscriptionId, payload.updatedSubscription);
      context.commit(SubscriptionMutations.SUBSCRIPTION_COMPLETE_WRITE_DATA);
      await context.dispatch(SubscriptionActions.FETCH_SUBSCRIPTION_BY_ID, { subscriptionId: payload.subscriptionId });
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
